import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { CongregationDashboard } from '~/modules/dashboard/CongregationDashboard';
import { StudyHelperDashboard } from '~/modules/dashboard/StudyHelperDashboard';

export const Route = createFileRoute('/_app/_dashboard/groups/$groupName/')({
  component: RouteComponent,
});

function RouteComponent(): ReactElement | null {
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/',
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data: currentGroup,
    isPending,
    isError,
  } = api.getGroupBySlug.useQuery({
    path: {
      group_slug: groupName,
    },
  });

  // Legacy's default group tab is `dashboard` for every type except social,
  // which 302s to activity (`bp-custom.php:81-85`
  // `filter_bp_groups_default_extension`; measured `GET /groups/da-club/` → 302
  // `/activity`). There is no social dashboard anywhere in legacy, so rendering
  // the congregation one here would show widgets — Locations, Students
  // Waiting — that it serves for a social group nowhere.
  const isSocial = currentGroup?.group_type === 'SOCIAL_GROUP';

  useEffect(() => {
    if (isSocial) {
      navigate({
        to: '/groups/$groupName/activity/',
        params: { groupName },
        replace: true,
      });
    }
  }, [isSocial, groupName, navigate]);

  // A refused or unknown group resolves the screen (W2.8 D5). The fall-through this
  // replaces reached `CongregationDashboard`, which issues no query at all and
  // hardcodes 3/0/2 over two mock widgets — three fabricated counts for a
  // caller the gate just declined. `$groupName.activity.tsx:86-89` already
  // answers the same class of caller this way, and legacy's own answer for a
  // non-member of a hidden group is `<title>Page not found</title>`.
  //
  // Necessary, not sufficient: this only fires when the slug read itself fails,
  // i.e. on a HIDDEN group. On a PUBLIC study group the slug read 200s for a
  // stranger and the refusal arrives on the stat query instead — see
  // `StudyHelperDashboard`'s own arm (W2.8 D14).
  if (isError) {
    return <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>;
  }

  // Hold the frame until the type is known: the last branch is no longer a
  // catch-all default, and mounting the congregation dashboard for a beat fires
  // its widget queries against a group that will never render it.
  if (isPending || isSocial) return null;

  return currentGroup?.group_type === 'STUDY_HELPER_STUDY_GROUP' ? (
    <StudyHelperDashboard />
  ) : (
    <CongregationDashboard groupName={groupName} />
  );
}
